import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { mplusStore } from 'entities/mplus/module/mplus-store'
import promoCodeStore from 'entities/promo-code/model/promo-code-store'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import findTicketStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import formPassengersStore from 'entities/ticket/models/form-passengers/form-passengers-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'
import { TYPE_TIMER } from 'entities/ticket/models/timer'
import timerFormStore from 'entities/ticket/models/timer/timer-store'
import userStore from 'entities/user/module/user-store'

import { BuyTicketApi } from 'shared/api/BuyTicket.ts'
import { BuyTicketResponse } from 'shared/api/BuyTicketModel.ts'
import { UserApi } from 'shared/api/UserApi'
import { CreditInfo } from 'shared/api/mplus/credit-info'
// import { PriceCertificate } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import ClockImage from 'shared/images/clock_image.png'
import ErrorImage from 'shared/images/error.png'
import { dateFormat } from 'shared/lib/dateFormat'
import { openDeepLink } from 'shared/lib/deep-link-open'
import { getErrorMessage } from 'shared/lib/errorText'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import HeaderPurchase from '../../components/Header'
import PriceChanged from '../../components/PriceChanged'
import { prepareDocuments } from '../form/lib/prepareDocuments'

import { ContactPassenger } from './components/contact-passenger'
import { FlightInfoV2 } from './components/flight-info-v2'
import { PassengerInfo } from './components/passenger-info'
import { PaymentMethod } from './components/payment-method'
import { PromoCodeAvia } from './components/promo-code-avia'
import { VisaInfo } from './components/visa-info'
import { onGetTicketFirstTouchFn } from './lib/first-touch-ordet-ticket'
import { isRegularRoute } from './lib/is-complex-route'
import { PaymentType } from './model'
import styles from './styles.module.scss'

interface TypePayment {
  dayInMonth?: number
  isCertificate?: boolean
  typePayment: PaymentType
}

export const Confirm = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const [loading, setLoading] = useState(false)
  const [isMplusAvailable, setIsMplusAvailable] = useState(false)

  const responseBuyTicket = useRef<BuyTicketResponse | null>(null)

  const selectedTicket = listTicketsStore.getSelectedTicket()
  const timerData = timerFormStore.getTimerData(TYPE_TIMER.FORM_PURCHASE)

  const finalPrice =
    Number(selectedTicket?.price.KGS || 0) +
    Number(formPassengersStore.calculateInsurancePrice())

  const countPassengerInfo = findTicketStore.getCountOfPassengers()

  const promoInfo = promoCodeStore.getPromoCode('avia')

  const checkCurrentValue = (
    response: BuyTicketResponse,
    onContinue?: () => void,
  ) => {
    responseBuyTicket.current = response

    openDialog({
      child: (
        <PriceChanged
          price={String(responseBuyTicket?.current?.price || '')}
          onBack={closeDialog}
          onContinue={() => {
            closeDialog()
            onContinue?.()
          }}
        />
      ),
      isDialog: false,
    })
  }

  const onStartPaymentTicket = async (props: TypePayment) => {
    if (loading) return null

    if (!timerData?.isRunning) {
      onShowTimeIsOut()

      return
    }

    setLoading(true)

    try {
      const { data: response } = await onGetTicketFirstTouchFn({
        selectedTicket: selectedTicket,
        formPassengers: formPassengersStore.formPassengersStore,
        selectedInsurances: formPassengersStore.selectedInsurances,
      })

      if (response?.data?.price_changed) {
        setLoading(false)

        checkCurrentValue(response?.data, () =>
          onIntermediateStep({ ...props, ...response?.data }),
        )

        return
      }

      onIntermediateStep({ ...props, ...response?.data })
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onIntermediateStep = async ({
    typePayment,
    dayInMonth,
    isCertificate,
    ...response
  }: TypePayment & BuyTicketResponse) => {
    setLoading(true)
    onSaveUsersInformation()

    switch (typePayment) {
      case 'regular':
        try {
          // if (isCertificate) {
          //   await BuyTicketApi.onBuyTicketByCertificate({
          //     service_id: response.id,
          //   })

          //   // Certificate covers the whole price: no payment needed
          //   if (PriceCertificate >= finalPrice) {
          //     await BuyTicketApi.onBuyTicketByConfirm({
          //       service_id: response.id,
          //     })

          //     navigate({
          //       pathname: RoutingPaths.avia.purchase.tickerCertificate.path(),
          //       search: `?service_id=${response.id}`,
          //     })

          //     return
          //   }
          // }

          if (promoInfo?.is_free_payment) {
            const { data } = await BuyTicketApi.buyTicketByCertificate({
              id: response.id,
              type: 'tickets',
              payment_provider: 9,
              promocode_id: promoInfo?.id,
            })

            navigate(RoutingPaths.avia.purchase.tickerCertificate.path(), {
              state: {
                payment_id: data?.payment_id,
              },
            })
          } else {
            const { data } = await BuyTicketApi.buyTicketConfirm({
              id: response.id,
              type: 'tickets',
              promocode_id: promoInfo?.id,
            })

            await startPaymentFromConfirmResult(data)
          }

          promoCodeStore.resetPromoInfo('avia')
        } catch (err) {
          const errorText = getErrorMessage(err)

          onShowError(errorText)
        } finally {
          setLoading(false)
        }

        break
      case 'mplus':
        if (!dayInMonth) return

        try {
          const { data: mplusResponse } = await CreditInfo.initPayMplus({
            service_id: response?.id,
            service_type: 'tickets',
            payment_day: dayInMonth,
          })

          if (mplusResponse?.data?.redirect_url) {
            openDeepLink(mplusResponse?.data?.redirect_url)
          } else {
            onShowError(t('payment_mplus_init_error'))
          }
        } catch (err) {
          const errorText = getErrorMessage(err)

          onShowError(errorText)
        } finally {
          setLoading(false)
        }

        break
      case 'mInstalment':
        try {
          const { data: mInstallmentResponse } =
            await CreditInfo.initPayMInstalment({
              service_id: response?.id,
              service_type: 'tickets',
            })

          if (mInstallmentResponse?.data?.redirect_url) {
            openDeepLink(mInstallmentResponse?.data?.redirect_url)
          } else {
            onShowError(t('minstallment.error_minstallment_text'))
          }
        } catch (err) {
          const errorText = getErrorMessage(err)

          onShowError(errorText)
        } finally {
          setLoading(false)
        }

        break
    }
  }

  const onUsePromoCode = () => {
    openDialog({
      isDialog: false,
      isClickable: false,
      child: (
        <PromoCodeAvia
          ticketBookInfo={{
            selectedTicket: selectedTicket,
            selectedInsurances: formPassengersStore.selectedInsurances,
          }}
          onAppliedPromoCode={(appliedPromo) => {
            promoCodeStore.setPromoCode('avia', appliedPromo)
            closeDialog()
          }}
        />
      ),
    })
  }

  const onShowTimeIsOut = () => {
    openDialog({
      child: (
        <PopupContent
          image={ClockImage}
          title={t('purchase_page.time_over')}
          description={t('purchase_page.time_over_desc')}
          button={t('apply')}
          onClick={() => {
            closeDialog()

            navigate(RoutingPaths.avia.search.path())
          }}
        />
      ),
      isDialog: false,
    })
  }

  const onSaveUsersInformation = async () => {
    const data = formPassengersStore.formPassengersStore

    if (data) {
      Boolean(data.email) && (await UserApi.updateEmail(data.email))

      findTicketFormStore.formFindTicket.forMySelf &&
        (await UserApi.updateUserDocuments(prepareDocuments(data?.adult?.[0])))

      userStore.saveEmail(data.email)
    }
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      isDialog: false,
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          button={t('try_again')}
          description={errorMessage || t('please_tey_again')}
          onClick={closeDialog}
        />
      ),
    })
  }

  const getMplusAvaliable = async () => {
    try {
      setLoading(true)
      const { data: mplusAvaliable } = await CreditInfo.getMplusAvaliable({
        tid: selectedTicket?.tid as string,
        uid: selectedTicket?.uid as string,
      })

      setIsMplusAvailable(mplusAvaliable?.data?.PaymentByMPlusAvailability)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onOpenMplus = () => {
    mplusStore.setPassengersForm(formPassengersStore.formPassengersStore)
    mplusStore.setInstallmentData({
      price: String(finalPrice),
      quantity: countPassengerInfo,
    })

    navigate(RoutingPaths.avia.mplus.path())
  }

  useEffect(() => {
    void getMplusAvaliable()
  }, [])

  const allPassengers = useMemo(
    () =>
      formPassengersStore.getListOfPassengers()?.map((passenger) => ({
        tid: selectedTicket?.tid || '',
        first_name: passenger?.firstName,
        last_name: passenger?.lastName,
        birthdate: dateFormat(
          passenger?.dateOfBirth,
          DATE_FORMAT_LIST.YEAR_FULL_DATE,
        ),
      })) || [],
    [formPassengersStore.formPassengersStore, selectedTicket?.tid],
  )

  useEffect(() => {
    if (!allPassengers.length) return

    if (selectedTicket?.is_check_for_black_list) {
      void formPassengersStore.getBlackListPassengers(allPassengers)
    }

    return () => {
      formPassengersStore.abortController?.abort()
    }
  }, [allPassengers])

  if (loading || !selectedTicket) {
    return (
      <div className={clsx('container', styles.wrapperLoader)}>
        <Loader />

        <p className={clsx(styles.text, 'bold-16')}>
          {t('purchase_page.loading_book')}
        </p>
      </div>
    )
  }

  const isUsePromoCodeAvailable =
    isRegularRoute(findTicketStore.formFindTicket?.directions) &&
    userStore.userSettings?.is_promocode_available
  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <HeaderPurchase title={t('purchase_page.check_data')} />
        </div>

        <FlightInfoV2
          selectedTicket={selectedTicket}
          countPassenger={countPassengerInfo}
          formFindTicket={findTicketStore.formFindTicket}
        />

        <PassengerInfo
          passengersInfo={formPassengersStore.formPassengersStore}
        />

        <ContactPassenger
          formPassengers={formPassengersStore.formPassengersStore}
        />

        <VisaInfo selectedTicket={selectedTicket} />
      </div>

      <PaymentMethod
        isLoading={loading}
        promoCode={promoInfo}
        finalPrice={finalPrice}
        selectedTicket={selectedTicket}
        isPromoAvailable={isUsePromoCodeAvailable}
        isMplusAvailable={isMplusAvailable}
        countPassengerInfo={countPassengerInfo}
        onUsePromoCode={onUsePromoCode}
        onOpenMplus={onOpenMplus}
        onRegularPay={(isCertificate) =>
          onStartPaymentTicket({ typePayment: 'regular', isCertificate })
        }
        onMplusPay={(dayInMonth) =>
          onStartPaymentTicket({ typePayment: 'mplus', dayInMonth })
        }
        onMInstalmentPayment={() =>
          onStartPaymentTicket({ typePayment: 'mInstalment' })
        }
      />
    </>
  )
})
